/* eslint-disable no-unused-expressions */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ZegoUIKitPrebuilt } from '@zegocloud/zego-uikit-prebuilt';
import { ZegoExpressEngine } from 'zego-express-engine-webrtc';
import './ZegoCloudStream.css';
import { storageKey } from '../BingoPage';
import { ZIM } from 'zego-zim-web';

const LIVESTREAM_BROADCAST_MESSAGE_TYPES = {
    DRAWN_NUMBER: 'drawnNumber',
    LIVESTREAM_VIEWERS_COUNT: 'viewersCount',
    ADD_COHOST: 'addCohost',
};

const LastDrawnNumber = ({ number }) => {
    return (
        <div className="latestDrawnBall mainNumber">
            {number}
        </div>
    )
}

export default function ZegoCloudStream({
    serverUrl,
    appId,
    zegoToken,
    userData,
    setCalledNumbers,
}) {

    const { messageToken, streamToken, streamRoomId, messageRoomId, userId1, userId2 } = zegoToken || {};
    const containerRef = useRef(null);
    const zpInstance = useRef(null);
    const zg = useRef(null);
    const currentRoomRef = useRef(false);
    const [isBallDrawn, setIsBallDrawn] = useState(null); // Use null for initial state
    const [isStreaming, setIsStreaming] = useState(false);
    const [role, setRole] = useState(ZegoUIKitPrebuilt.Audience);
    const [shouldRejoin, setShouldRejoin] = useState(false);
    const kitToken = useRef(null);

    // Generate kit token with proper validation
    useEffect(() => {
        if (!appId || !streamToken || !streamRoomId || !userId1) {
            console.error('Missing required token parameters');
            return;
        }
        try {
            kitToken.current = ZegoUIKitPrebuilt.generateKitTokenForProduction(
                appId,
                streamToken,
                streamRoomId,
                userId1,
                userData?.username ?? 'Guest',
            );
        } catch (err) {
            console.error('Failed to generate kit token:', err);
        }
    }, [appId, streamToken, streamRoomId, userId1, userData]);

    // Join room logic with validation
    const joinRoom = useCallback(() => {
        if (!containerRef.current || !zpInstance.current || !kitToken.current) {
            console.log('Cannot join room: missing container, zpInstance, or kitToken');
            return;
        }

        try {
            zpInstance.current.joinRoom({
                container: containerRef.current,
                scenario: {
                    mode: ZegoUIKitPrebuilt.LiveStreaming,
                    config: { role },
                },
                showPreJoinView: false,
                showLeavingView: true,
                showRoomTimer: false,
                showUserList: false,
                showTextChat: false,
                showInviteToCohostButton: false,
                showRemoveCohostButton: false,
                showRequestToCohostButton: false,
                showNonVideoUser: false,
                showOnlyAudioUser: false,
                lowerLeftNotification: {
                    showUserJoinAndLeave: false,
                    showTextChat: false,
                },
                turnOnMicrophoneWhenJoining: false,
                turnOnCameraWhenJoining: false,
                useFrontFacingCamera: false,
                showRoomDetailsButton: false,
                videoScreenConfig: {
                    objectFit: 'cover',
                },
                onLiveStart: () => setIsStreaming(true),
                onLiveEnd: () => {
                    setIsStreaming(false);
                    setRole(ZegoUIKitPrebuilt.Audience);
                },
                onJoinRoom: () => {
                    setIsStreaming(true);
                    currentRoomRef.current = true;
                },
                onLeaveRoom: () => {
                    setIsStreaming(false);
                    currentRoomRef.current = false;
                },
            });
        } catch (error) {
            console.error('Failed to join room:', error);
        }
    }, [role]);

    // Cleanup logic
    const leaveRoom = useCallback(() => {
        try {
            if (zpInstance.current) {
                zpInstance.current.destroy();
                zpInstance.current = null;
            }
            if (zg.current && messageRoomId) {
                zg.current.logoutRoom(messageRoomId);
                zg.current.destroyEngine();
                zg.current = null;
            }
        } catch (err) {
            console.error('Error during cleanup:', err);
        }
    }, [messageRoomId]);

    // Initialize Zego and join room
    const initializeAndJoinRoom = useCallback(async () => {

        if (!containerRef.current || !kitToken.current || !appId || !serverUrl) {
            console.log('Cannot initialize: missing required parameters');
            return;
        }

        try {

            zpInstance.current = ZegoUIKitPrebuilt.create(kitToken.current);

            try {
                await zpInstance.current.addPlugins({ ZIM });
            } catch (err) {
                console.log('Failed to add ZIM plugin:', err);
            }

            joinRoom();
        } catch (error) {
            console.error('Error initializing Zego:', error);
        }
    }, [appId, serverUrl, joinRoom]);

    // Watch stream and handle messages
    const watchStream = useCallback(async () => {
        zg.current = new ZegoExpressEngine(appId, serverUrl);
        if (!zg.current || !messageRoomId || !messageToken || !userId2) {
            console.log('Cannot watch stream: missing required parameters');
            return;
        }

        try {
            await zg.current.loginRoom(
                messageRoomId,
                messageToken,
                {
                    userID: userId2,
                    userName: userData?.username ?? 'Guest',
                },
                { userUpdate: true },
            );

            zg.current.on('IMRecvBroadcastMessage', (roomID, messageList) => {
                try {
                    if (!messageList?.[0]?.message) return;

                    const parsedMessage = JSON.parse(messageList[0].message);
                    const { type, value } = parsedMessage || {};

                    if (type === LIVESTREAM_BROADCAST_MESSAGE_TYPES.DRAWN_NUMBER) {
                        const { lastDrawnNumber, ballType } = value || {};
                        setIsBallDrawn({ lastDrawnNumber, ballType });
                        setCalledNumbers((prev) => {
                            try {
                                const updatedNumbers = [...prev, lastDrawnNumber];
                                window.localStorage.setItem(storageKey, JSON.stringify(updatedNumbers));
                                return updatedNumbers;
                            } catch (err) {
                                console.error('Error saving to localStorage:', err);
                                return prev;
                            }
                        });
                    } else if (type === LIVESTREAM_BROADCAST_MESSAGE_TYPES.ADD_COHOST) {
                        if (userId1 === value) {
                            setRole(ZegoUIKitPrebuilt.Cohost);
                            setShouldRejoin(true);
                        }
                    }
                } catch (err) {
                    console.error('Error processing broadcast message:', err);
                }
            });
        } catch (err) {
            console.error('Failed to login to Zego room:', err);
        }
    }, [messageRoomId, messageToken, userId1, userId2, userData, setCalledNumbers]);

    // Main initialization effect
    useEffect(() => {
        const initialize = async () => {
            try {
                await initializeAndJoinRoom();
                await watchStream();
            } catch (error) {
                console.error('Error in stream initialization:', error);
            }
        };

        initialize();

        return () => {
            leaveRoom();
        };
    }, [initializeAndJoinRoom, watchStream, leaveRoom, appId, serverUrl]);

    // Handle rejoin logic
    useEffect(() => {
        if (shouldRejoin && currentRoomRef.current) {
            leaveRoom();
            const timer = setTimeout(() => {
                initializeAndJoinRoom();
                setShouldRejoin(false);
            }, 500);

            return () => clearTimeout(timer);
        }
    }, [shouldRejoin, initializeAndJoinRoom, leaveRoom]);

    return (
        <div
            className="w-full 2xl:max-w-[360px] lg:max-w-[250px] xl:min-w-[280px] lg:min-w-[200px] 2xl:min-w-[360px] md:min-w-[170px] max-w-[180px] zego-video-container  relative"
            ref={containerRef}
        >
            {isStreaming && isBallDrawn?.lastDrawnNumber && (
                <LastDrawnNumber key={isBallDrawn?.lastDrawnNumber} number={isBallDrawn?.lastDrawnNumber} />
            )}
        </div>
    );
}